<template>
  <nav class="board-paging" aria-label="페이지 이동">
    <!-- 맨 처음 -->
    <a
      class="board-paging__btn_first"
      :class="{ disabled: currentPage === 1 }"
      @click.prevent="goToPage(1)"
    >
      <i class="svg_icon icon_last" aria-hidden="true"></i>
      <span class="sr-only">맨 처음 페이지</span>
    </a>

    <!-- 이전 -->
    <a
      class="board-paging__btn_prev"
      :class="{ disabled: currentPage === 1 }"
      @click.prevent="goToPage(currentPage - 1)"
    >
      <i class="svg_icon icon_next" aria-hidden="true"></i>
      <span class="sr-only">이전 페이지</span>
    </a>

    <!-- 페이지 목록 -->
    <a
      v-for="page in visiblePages"
      :key="page"
      class="board-paging__page"
      :class="{ 'is-active': page === currentPage }"
      @click.prevent="goToPage(page)"
      :aria-label="`${page}페이지`"
      :aria-current="page === currentPage ? 'page' : undefined"
    >
      {{ page }}
    </a>

    <!-- 다음 -->
    <a
      class="board-paging__btn_next"
      :class="{ disabled: currentPage === totalPages }"
      @click.prevent="goToPage(currentPage + 1)"
    >
      <i class="svg_icon icon_next" aria-hidden="true"></i>
      <span class="sr-only">다음 페이지</span>
    </a>

    <!-- 맨 마지막 -->
    <a
      class="board-paging__btn_last"
      :class="{ disabled: currentPage === totalPages }"
      @click.prevent="goToPage(totalPages)"
    >
      <i class="svg_icon icon_last" aria-hidden="true"></i>
      <span class="sr-only">맨 마지막 페이지</span>
    </a>
  </nav>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  modelValue: {
    type: Number,
    default: 1,
  },
  totalItems: {
    type: [Number, String],
    required: true,
  },
  itemsPerPage: {
    type: Number,
    default: 10,
  },
  visibleCount: {
    type: Number,
    default: 10, // 한 번에 보이는 페이지 개수 유지
  },
});

const emit = defineEmits(["update:modelValue"]);

// 항상 1페이지를 디폴트로 보장
const currentPage = computed(() =>
  props.modelValue < 1 ? 1 : props.modelValue
);

// 전체 페이지 수 계산
const totalPages = computed(() => {
  const totalItemsNum = Number(String(props.totalItems).replace(/,/g, "")) || 0;
  return Math.max(1, Math.ceil(totalItemsNum / props.itemsPerPage));
});
// 5개씩 보이게 하는 계산 로직 유지
const visiblePages = computed(() => {
  const half = Math.floor(props.visibleCount / 2);
  let start = Math.max(1, currentPage.value - half);
  let end = start + props.visibleCount - 1;
  if (end > totalPages.value) {
    end = totalPages.value;
    start = Math.max(1, end - props.visibleCount + 1);
  }
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
});

// 페이지 이동
const goToPage = (page) => {
  if (page < 1 || page > totalPages.value || page === currentPage.value) return;
  emit("update:modelValue", page);
};
</script>

<style scoped>
/* 비활성화 상태일 때 클릭 방지 및 시각적 표시 */
.disabled {
  pointer-events: none;
  opacity: 0.4;
}
</style>
